import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useId, useRef, useState, memo } from 'react';
import { ResizableBox } from 'react-resizable';
import Draggable from 'react-draggable';
import classNames from 'classnames';

import { usePopupContext } from '../../../contexts/PopupContext';
import { Spinner } from '../Spinner';

import useFocusTrap from './useFocusTrap';
import style from './style.module.css';

/**
 * Helper for popup's building.
 *
 * @param props {Object} - Popup's props.
 * @param props.id {String}.
 * @param [props.header = null] {String|React.Element} - Content of Popup's header's <h4> tag.
 * @param props.children {React.Element|React.Component} - Popup's content.
 * @param [props.footer = null] {String|React.Element[]|React.Element} - Content of Popup's header's <h4> tag.
 * @param [props.subheaderClassName] {String} - Extra class for the subheader wrapper.
 * @param [props.onClose] {Function} - Close handler; replaces the global popup closing (for nested popups).
 * @param [props.width = 600] {Number} - Dialog width used to center the initial position.
 */
const PopupComponent = ({
  id,
  header = null,
  children,
  footer = null,
  loading = false,
  subheader = null,
  subheaderClassName,
  className,
  onClose,
  width = 600,
}) => {
  const { hasPopup } = usePopupContext();
  const dialogRef = useRef(null);
  const draggableRef = useRef(null);
  const titleId = useId();
  const defaultPosition = {
    x: Math.floor((window.innerWidth - width) / 2),
    y: 25,
  };

  const classNameModal = classNames('move modal fade in', className, hasPopup() ? style.enter : style.exit);

  const footerContent = footer === null ? footer : <PopupFooter>{footer}</PopupFooter>;
  const renderSubheader = subheader && <div>{subheader}</div>;

  useFocusTrap(dialogRef);

  /**
   * Prevent lagging by adding a div element under Draggable element.
   */
  return (
    <div
      ref={dialogRef}
      aria-labelledby={titleId}
      aria-modal="true"
      className={classNameModal}
      id={id}
      role="dialog"
      style={{ display: 'block' }}
      tabIndex="-1"
    >
      <Draggable
        axis="both"
        bounds="parent"
        defaultPosition={defaultPosition}
        handle=".handle-draggable"
        nodeRef={draggableRef}
        position={null}
      >
        <div ref={draggableRef} className="modal-dialog" role="document">
          <div className="modal-content">
            <PopupHeader onClose={onClose} titleId={titleId} draggable>
              {header}
              <div className={classNames(style.subheader, subheaderClassName)}>{renderSubheader}</div>
            </PopupHeader>

            <div className="modal-body">{children}</div>

            {footerContent}
            {loading && <Spinner className="spinner--overlay" />}
          </div>
        </div>
      </Draggable>
    </div>
  );
};

PopupComponent.propTypes = {
  children: PropTypes.node.isRequired,
  footer: PropTypes.element,
  header: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
  id: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  onClose: PropTypes.func,
  subheaderClassName: PropTypes.string,
  width: PropTypes.number,
};

const Popup = memo(PopupComponent);

const DEFAULT_MIN_CONSTRAINTS = [500, 450];

const getMaxConstraints = () => {
  const maxWidth = Math.max(document.documentElement.clientWidth, window.innerWidth || 500) - 20;
  const maxHeight = Math.max(document.documentElement.clientHeight, window.innerHeight || 450) - 50;

  return [maxWidth, maxHeight];
};

/**
 * Helper for popup's building.
 *
 * @param props {Object} - Popup's props.
 * @param props.id {String}.
 * @param [props.header = null] {String|React.Element} - Content of Popup's header's <h4> tag.
 * @param props.children {React.Element|React.Component} - Popup's content.
 * @param [props.footer = null] {String|React.Element[]|React.Element} - Content of Popup's header's <h4> tag.
 */
const ResizablePopupComponent = ({
  id,
  header = null,
  children,
  footer = null,
  loading = false,
  minConstraints = DEFAULT_MIN_CONSTRAINTS,
  onResizeStop,
  width,
  height,
  defaultPosition,
}) => {
  const { hasPopup } = usePopupContext();

  // Initialised once from props — stable refs, no re-render on change.
  const widthRef = useRef(width);
  const heightRef = useRef(height);
  const defaultPositionRef = useRef(
    defaultPosition || {
      x: Math.floor((window.innerWidth - 600) / 2),
      y: 25,
    },
  );

  const dialogRef = useRef(null);
  const draggableRef = useRef(null);
  const titleId = useId();
  const [maxConstraints, setMaxConstraints] = useState(getMaxConstraints);

  const updateMaxConstraints = useCallback(() => {
    setMaxConstraints(getMaxConstraints());
  }, []);

  useEffect(() => {
    window.addEventListener('resize', updateMaxConstraints);

    return () => {
      window.removeEventListener('resize', updateMaxConstraints);
    };
  }, [updateMaxConstraints]);

  useFocusTrap(dialogRef);

  const className = classNames('modal fade in', hasPopup() ? style.enter : style.exit);
  const footerContent = footer === null ? footer : <PopupFooter>{footer}</PopupFooter>;

  return (
    <div
      ref={dialogRef}
      aria-labelledby={titleId}
      aria-modal="true"
      className={className}
      id={id}
      role="dialog"
      style={{ display: 'block' }}
      tabIndex="-1"
    >
      <Draggable
        axis="both"
        bounds="parent"
        defaultPosition={defaultPositionRef.current}
        handle=".handle-draggable"
        nodeRef={draggableRef}
        onDrag={event => event.preventDefault()}
        position={null}
      >
        <div ref={draggableRef}>
          <ResizableBox
            className="modal-dialog"
            height={heightRef.current}
            maxConstraints={maxConstraints}
            minConstraints={minConstraints}
            onResizeStop={onResizeStop}
            role="document"
            width={widthRef.current}
          >
            <div className={classNames(style.resizableContent, 'modal-content h100p')}>
              <PopupHeader titleId={titleId} draggable noWrapText>
                {header}
              </PopupHeader>
              <div className={classNames(style.resizableBody, 'modal-body')}>{children}</div>
              {footerContent}
              {loading && <Spinner className="spinner--overlay" />}
            </div>
          </ResizableBox>
        </div>
      </Draggable>
    </div>
  );
};

ResizablePopupComponent.propTypes = {
  children: PropTypes.node,
  footer: PropTypes.element,
  header: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
  height: PropTypes.number.isRequired,
  id: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  minConstraints: PropTypes.array,
  onResizeStop: PropTypes.func,
  width: PropTypes.number.isRequired,
};

/**
 * Popup footer's wrapper.
 *
 * @param props {Object}.
 * @param props.children {*} Popup footer content.
 */
const PopupFooter = props => {
  const { children } = props;

  return <div className="modal-footer">{children}</div>;
};

PopupFooter.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Popup header's wrapper.
 *
 * @param props {Object} Popup header's props.
 * @param [props.children=''] {*} Popup header's content. Will be wrapped into `<h4 class="modal-title">`.
 * @param [props.onClose] {Function} Close handler; replaces the global popup closing.
 */
const PopupHeader = props => {
  const { children = '', className = '', noWrapText = false, draggable = false, onRef, onClose, titleId } = props;
  const { closePopup, isClosablePopup } = usePopupContext();

  const headerButton =
    onClose || isClosablePopup() ? (
      <button aria-label="Close" className="close" onClick={onClose || closePopup} type="button">
        <span aria-hidden="true">×</span>
      </button>
    ) : null;
  const headClass = classNames('modal-header', className, {
    'truncate-text': noWrapText,
    'handle-draggable': draggable,
  });

  return (
    <div ref={onRef} className={headClass}>
      {headerButton}
      <h4 className="modal-title" id={titleId}>
        {children}
      </h4>
    </div>
  );
};

PopupHeader.propTypes = {
  children: PropTypes.node,
  onClose: PropTypes.func,
  titleId: PropTypes.string,
};

const ResizablePopup = memo(ResizablePopupComponent);

export default Popup;
export { ResizablePopup };
